import React, {useState, useMemo} from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import SchoolIcon from "@mui/icons-material/School";
import GroupsIcon from "@mui/icons-material/Groups";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VideocamIcon from "@mui/icons-material/Videocam";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import {Helmet} from "react-helmet";
import {useNavigate, useSearchParams} from "react-router";
import {useCheckIfAdmin} from "../firebase/useCheckIfAdmin";
import {useIsInstructorOrAdmin} from "../firebase/useIsInstructorOrAdmin";
import {useUnifiedCourses} from "./useUnifiedCourses";
import {InstructorTrainingCard} from "./InstructorTrainingCard";
import {CourseInterestModal} from "../instructors/CourseInterestModal";
import {COURSE_STATUS_LABELS, COURSE_STATUS_COLORS} from "../instructors/courseValidation";

export const UnifiedCalendarPage = () => {
    const navigate = useNavigate();
    const isAdmin = useCheckIfAdmin();
    const isInstructor = useIsInstructorOrAdmin();
    const [searchParams, setSearchParams] = useSearchParams();

    // Tab state from URL: tab=fagfolk or tab=innbyggere
    const [activeTab, setActiveTab] = useState(() => (searchParams.get("tab") === "innbyggere" ? 1 : 0));

    React.useEffect(() => {
        setActiveTab(searchParams.get("tab") === "innbyggere" ? 1 : 0);
    }, [searchParams]);

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCounty, setSelectedCounty] = useState("Alle");
    const [selectedFormat, setSelectedFormat] = useState("Alle");

    const [interestModalInstructor, setInterestModalInstructor] = useState(null);
    const [interestModalCourse, setInterestModalCourse] = useState(null);

    const {
        filteredProfessionalTrainings,
        professionalTrainings,
        filteredCitizenCourses,
        citizenCourses,
    } = useUnifiedCourses({
        searchTerm,
        selectedCounty,
        selectedFormat: selectedFormat === "Alle" ? "Alle" : selectedFormat.toLowerCase(),
    });

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        const nextParams = new URLSearchParams(searchParams);
        if (newValue === 1) {
            nextParams.set("tab", "innbyggere");
        } else {
            nextParams.set("tab", "fagfolk");
        }
        setSearchParams(nextParams);
    };

    // Extract counties with active citizen courses
    const availableCounties = useMemo(() => {
        const countyMap = new Map();
        citizenCourses.forEach((c) => {
            if (c.county) {
                countyMap.set(c.county, (countyMap.get(c.county) || 0) + 1);
            }
        });
        return Array.from(countyMap.entries()).map(([county, count]) => ({ county, count }));
    }, [citizenCourses]);

    return (
        <Box sx={{py: {xs: 4, md: 7}, bgcolor: "#FAFAF9", minHeight: "85vh"}}>
            <Helmet>
                <title>Kurskalender | Bekymringsmestring</title>
                <meta
                    name="description"
                    content="Samlet kurskalender for kurslederopplæring for fagpersoner og lokale kurs i bekymringsmestring for innbyggere."
                />
            </Helmet>

            <Container maxWidth="lg">
                {/* Header */}
                <Box sx={{textAlign: "center", mb: 4}}>
                    <Box sx={{display: "inline-flex", alignItems: "center", gap: 1.5, mb: 1.5}}>
                        <CalendarMonthIcon color="primary" sx={{fontSize: {xs: 32, sm: 40}}} />
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{fontWeight: 800, color: "text.primary", letterSpacing: "-0.02em"}}
                        >
                            Kurskalender
                        </Typography>
                    </Box>
                    <Typography
                        variant="h6"
                        component="p"
                        color="text.secondary"
                        sx={{maxWidth: 750, mx: "auto", fontWeight: 400, fontSize: {xs: "1rem", sm: "1.15rem"}}}
                    >
                        Finn sertifiserende opplæring for helsepersonell og lokale mestringskurs for innbyggere i hele landet.
                    </Typography>

                    {/* Admin / Instructor Quick Action Toolbar */}
                    {(isAdmin || isInstructor) && (
                        <Box
                            sx={{
                                mt: 2.5,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 1.5,
                            }}
                        >
                            {isAdmin && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    startIcon={<SchoolIcon />}
                                    onClick={() => navigate("/admin/training")}
                                    sx={{
                                        borderRadius: 2,
                                        fontWeight: 700,
                                        textTransform: "none",
                                        minHeight: 40,
                                        px: 2.5,
                                        boxShadow: "0 4px 12px rgba(40, 82, 56, 0.15)",
                                    }}
                                >
                                    Administrer kurslederopplæring
                                </Button>
                            )}
                            {isInstructor && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    startIcon={<EventAvailableIcon />}
                                    onClick={() => navigate("/me")}
                                    sx={{
                                        borderRadius: 2,
                                        fontWeight: 700,
                                        textTransform: "none",
                                        minHeight: 40,
                                        px: 2.5,
                                        bgcolor: "background.paper",
                                    }}
                                >
                                    Administrer mine kurs & henvendelser
                                </Button>
                            )}
                        </Box>
                    )}
                </Box>

                {/* Dual Audience Navigation Tabs */}
                <Box sx={{borderBottom: 1, borderColor: "divider", mb: 4}}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        aria-label="Velg målgruppe for kurskalender"
                        centered
                        textColor="primary"
                        indicatorColor="primary"
                        sx={{
                            "& .MuiTab-root": {
                                textTransform: "none",
                                fontWeight: 700,
                                fontSize: {xs: "0.95rem", sm: "1.1rem"},
                                minHeight: 56,
                                px: {xs: 2, sm: 4},
                            },
                        }}
                    >
                        <Tab
                            icon={<SchoolIcon />}
                            iconPosition="start"
                            label={`For fagfolk (${professionalTrainings.length})`}
                            id="tab-professionals"
                            aria-controls="tabpanel-professionals"
                        />
                        <Tab
                            icon={<GroupsIcon />}
                            iconPosition="start"
                            label={`For innbyggere (${citizenCourses.length})`}
                            id="tab-citizens"
                            aria-controls="tabpanel-citizens"
                        />
                    </Tabs>
                </Box>

                {/* Common Filter Controls */}
                <Box sx={{mb: 4, width: "100%"}}>
                    <Box sx={{display: "flex", flexDirection: {xs: "column", sm: "row"}, gap: 2, mb: 2}}>
                        <TextField
                            fullWidth
                            size="medium"
                            placeholder={activeTab === 0 ? "Søk etter kurs, kursholder..." : "Søk etter sted, kursholder..."}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            inputProps={{ "aria-label": "Søk i kalenderen" }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            aria-label="Tøm søk"
                                            onClick={() => setSearchTerm("")}
                                            sx={{
                                                minWidth: 44,
                                                minHeight: 44,
                                                "&:focus-visible": {
                                                    outline: "2px solid #285238",
                                                    outlineOffset: "2px",
                                                },
                                            }}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                            sx={{bgcolor: "background.paper", borderRadius: 2}}
                        />
                    </Box>

                    {/* Filter chips: Format + Counties (if on citizen tab) */}
                    <Box
                        sx={{display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center"}}
                        role="group"
                        aria-label="Filter for kursformat og fylke"
                    >
                        <Typography variant="body2" sx={{fontWeight: 700, mr: 0.5, color: "text.secondary"}}>
                            Format:
                        </Typography>
                        {["Alle", "Digital", "Fysisk"].map((format) => {
                            const isSelected = selectedFormat === format;
                            return (
                                <Chip
                                    key={format}
                                    label={format}
                                    clickable
                                    aria-pressed={isSelected}
                                    color={isSelected ? "primary" : "default"}
                                    variant={isSelected ? "filled" : "outlined"}
                                    onClick={() => setSelectedFormat(format)}
                                    sx={{
                                        fontWeight: 600,
                                        minHeight: 44,
                                        height: "auto",
                                        py: 0.75,
                                        px: 1,
                                        borderRadius: 2,
                                        "&:focus-visible": {
                                            outline: "2px solid #285238",
                                            outlineOffset: "2px",
                                        },
                                    }}
                                />
                            );
                        })}

                        {activeTab === 1 && availableCounties.length > 0 && (
                            <>
                                <Typography variant="body2" sx={{fontWeight: 700, ml: {sm: 2}, mr: 0.5, color: "text.secondary"}}>
                                    Fylke:
                                </Typography>
                                <Chip
                                    label="Alle"
                                    clickable
                                    aria-pressed={selectedCounty === "Alle"}
                                    color={selectedCounty === "Alle" ? "primary" : "default"}
                                    variant={selectedCounty === "Alle" ? "filled" : "outlined"}
                                    onClick={() => setSelectedCounty("Alle")}
                                    sx={{
                                        fontWeight: 600,
                                        minHeight: 44,
                                        height: "auto",
                                        py: 0.75,
                                        px: 1,
                                        borderRadius: 2,
                                        "&:focus-visible": {
                                            outline: "2px solid #285238",
                                            outlineOffset: "2px",
                                        },
                                    }}
                                />
                                {availableCounties.map(({county, count}) => {
                                    const isCountySelected = selectedCounty === county;
                                    return (
                                        <Chip
                                            key={county}
                                            label={`${county} (${count})`}
                                            clickable
                                            aria-pressed={isCountySelected}
                                            aria-label={`Filtrer på ${county}, ${count} ${
                                                count === 1 ? "kurs" : "kurs"
                                            }`}
                                            color={isCountySelected ? "primary" : "default"}
                                            variant={isCountySelected ? "filled" : "outlined"}
                                            onClick={() => setSelectedCounty(county)}
                                            sx={{
                                                fontWeight: 600,
                                                minHeight: 44,
                                                height: "auto",
                                                py: 0.75,
                                                px: 1,
                                                borderRadius: 2,
                                                "&:focus-visible": {
                                                    outline: "2px solid #285238",
                                                    outlineOffset: "2px",
                                                },
                                            }}
                                        />
                                    );
                                })}
                            </>
                        )}
                    </Box>
                </Box>

                {/* TAB 0: FOR FAGFOLK (Kurslederopplæring) */}
                {activeTab === 0 && (
                    <Box role="tabpanel" id="tabpanel-professionals" aria-labelledby="tab-professionals">
                        {isAdmin && (
                            <Box sx={{display: "flex", justifyContent: {xs: "stretch", sm: "flex-end"}, mb: 2.5}}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<AddCircleOutlineIcon />}
                                    onClick={() => navigate("/admin/training")}
                                    sx={{
                                        borderRadius: 2,
                                        fontWeight: 700,
                                        textTransform: "none",
                                        minHeight: 42,
                                        px: 2.5,
                                        width: {xs: "100%", sm: "auto"},
                                    }}
                                >
                                    Opprett / rediger kurslederopplæring
                                </Button>
                            </Box>
                        )}
                        {filteredProfessionalTrainings.length > 0 ? (
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {xs: "1fr", md: "repeat(2, 1fr)"},
                                    gap: 3,
                                }}
                            >
                                {filteredProfessionalTrainings.map((training) => (
                                    <InstructorTrainingCard key={training.id} training={training} />
                                ))}
                            </Box>
                        ) : (
                            <Card sx={{p: 5, textAlign: "center", borderRadius: 3, border: "1px dashed", borderColor: "divider"}}>
                                <Typography variant="h6" color="text.secondary" sx={{fontWeight: 600, mb: 1}}>
                                    Ingen kurslederopplæringer matchet søket
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{mb: isAdmin ? 2.5 : 0}}>
                                    Prøv å endre søkeord eller formatfilter.
                                </Typography>
                                {isAdmin && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<AddCircleOutlineIcon />}
                                        onClick={() => navigate("/admin/training")}
                                        sx={{
                                            borderRadius: 2,
                                            fontWeight: 700,
                                            textTransform: "none",
                                            minHeight: 44,
                                            px: 3,
                                        }}
                                    >
                                        Opprett ny kurslederopplæring
                                    </Button>
                                )}
                            </Card>
                        )}
                    </Box>
                )}

                {/* TAB 1: FOR INNBYGGERE (Lokale kurs) */}
                {activeTab === 1 && (
                    <Box role="tabpanel" id="tabpanel-citizens" aria-labelledby="tab-citizens">
                        {isInstructor && (
                            <Box sx={{display: "flex", justifyContent: {xs: "stretch", sm: "flex-end"}, mb: 2.5}}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<EventAvailableIcon />}
                                    onClick={() => navigate("/me")}
                                    sx={{
                                        borderRadius: 2,
                                        fontWeight: 700,
                                        textTransform: "none",
                                        minHeight: 42,
                                        px: 2.5,
                                        width: {xs: "100%", sm: "auto"},
                                    }}
                                >
                                    Opprett / administrer mine kurs
                                </Button>
                            </Box>
                        )}
                        {filteredCitizenCourses.length > 0 ? (
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)"},
                                    gap: 3,
                                }}
                            >
                                {filteredCitizenCourses.map((course) => {
                                    const statusLabel = COURSE_STATUS_LABELS[course.status] || course.status;
                                    const statusColor = COURSE_STATUS_COLORS[course.status] || "default";

                                    return (
                                        <Card
                                            key={`${course.instructorId}-${course.id}`}
                                            elevation={0}
                                            sx={{
                                                borderRadius: 3,
                                                border: "1px solid rgba(40, 82, 56, 0.12)",
                                                display: "flex",
                                                flexDirection: "column",
                                                boxShadow: "0 4px 14px rgba(0, 0, 0, 0.04)",
                                                transition: "all 0.2s ease-in-out",
                                                "&:hover": {
                                                    boxShadow: "0 8px 24px rgba(40, 82, 56, 0.08)",
                                                    borderColor: "primary.main",
                                                },
                                            }}
                                        >
                                            <CardContent sx={{p: 2.5, flex: "1 0 auto"}}>
                                                <Box sx={{display: "flex", justifyContent: "space-between", mb: 1.5}}>
                                                    <Chip
                                                        icon={course.isDigital ? <VideocamIcon /> : <LocationOnIcon />}
                                                        label={course.isDigital ? "Digitalt" : course.city || "Fysisk"}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{fontWeight: 600}}
                                                    />
                                                    <Chip
                                                        label={statusLabel}
                                                        color={statusColor}
                                                        size="small"
                                                        sx={{fontWeight: 700}}
                                                    />
                                                </Box>

                                                <Typography variant="h6" component="h3" sx={{fontWeight: 700, mb: 1}}>
                                                    {course.title}
                                                </Typography>

                                                <Typography variant="body2" sx={{color: "text.secondary", mb: 1.5}}>
                                                    Oppstart: <strong>{course.startDate}</strong>
                                                </Typography>

                                                {course.location && (
                                                    <Typography variant="caption" sx={{display: "block", color: "text.secondary", mb: 1.5}}>
                                                        Sted: {course.location}
                                                    </Typography>
                                                )}

                                                <Box sx={{mt: "auto", pt: 1.5, borderTop: "1px solid rgba(40, 82, 56, 0.08)"}}>
                                                    <Typography variant="body2" sx={{fontWeight: 700, color: "primary.main"}}>
                                                        Kursholder: {course.instructorName}
                                                    </Typography>
                                                    {course.instructorTitle && (
                                                        <Typography variant="caption" sx={{color: "text.secondary"}}>
                                                            {course.instructorTitle}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </CardContent>

                                            <Box sx={{p: 2, pt: 0, display: "flex", flexDirection: "column", gap: 1}}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    startIcon={<ContactMailIcon />}
                                                    onClick={() => {
                                                        setInterestModalInstructor({
                                                            id: course.instructorId,
                                                            name: course.instructorName,
                                                            email: course.instructorEmail,
                                                            phone: course.instructorPhone,
                                                            place: course.instructorPlace,
                                                            city: course.city,
                                                            county: course.county,
                                                            upcomingCourses: [course],
                                                        });
                                                        setInterestModalCourse(course);
                                                    }}
                                                    sx={{
                                                        minHeight: 44,
                                                        fontWeight: 700,
                                                        textTransform: "none",
                                                        borderRadius: 2,
                                                    }}
                                                >
                                                    Meld interesse for kurs
                                                </Button>
                                            </Box>
                                        </Card>
                                    );
                                })}
                            </Box>
                        ) : (
                            <Card sx={{p: 5, textAlign: "center", borderRadius: 3, border: "1px dashed", borderColor: "divider"}}>
                                <Typography variant="h6" color="text.secondary" sx={{fontWeight: 600, mb: 1}}>
                                    Ingen lokale kurs funnet
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{mb: isInstructor ? 2.5 : 0}}>
                                    Prøv å velge et annet fylke eller nullstille filtrene.
                                </Typography>
                                {isInstructor && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<EventAvailableIcon />}
                                        onClick={() => navigate("/me")}
                                        sx={{
                                            borderRadius: 2,
                                            fontWeight: 700,
                                            textTransform: "none",
                                            minHeight: 44,
                                            px: 3,
                                        }}
                                    >
                                        Gå til Min side for å legge ut kurs
                                    </Button>
                                )}
                            </Card>
                        )}
                    </Box>
                )}
            </Container>

            {/* Interest Modal */}
            <CourseInterestModal
                open={Boolean(interestModalInstructor)}
                instructor={interestModalInstructor}
                selectedCourse={interestModalCourse}
                onClose={() => {
                    setInterestModalInstructor(null);
                    setInterestModalCourse(null);
                }}
            />
        </Box>
    );
};
